<script>
    import { onMount } from 'svelte';
    import { 
        fetchCurrentCashier,
        openCashier,
        addCashierMovement,
        closeCashier,
        getCashierReceiptUrl,
        API_BASE,
        getCurrentStore
    } from '../services/api';
    import Icon from '../components/icons/Icon.svelte';

    export let onNotify = (msg, type) => {};

    const store = getCurrentStore() || { id: '', name: 'Restaurante' };

    let isLoading = true;

    // Dados de Caixa
    let cashierData = {
        isBoxOpen: false,
        shift: null,
        summary: {
            initialCash: 0,
            cashSales: 0,
            pixSales: 0,
            cardSales: 0,
            totalRevenue: 0,
            supplements: 0,
            sangrias: 0,
            expectedCash: 0,
            ordersCount: 0
        },
        movements: []
    };

    // Modais
    let showOpenCashierModal = false;
    let openOperatorName = '';
    let openInitialCash = '';
    let openNotes = '';

    let showMovementModal = false;
    let movementType = 'sangria'; // 'sangria' ou 'suprimento'
    let movementAmount = '';
    let movementReason = '';

    let showCloseCashierModal = false;
    let countedCash = '';
    let closeNotes = '';

    onMount(async () => {
        await loadCashier();
    });

    async function loadCashier() {
        isLoading = true;
        try {
            const res = await fetchCurrentCashier();
            if (res) {
                cashierData = res;
            }
        } catch (err) {
            console.warn('Erro ao buscar caixa:', err);
        } finally {
            isLoading = false;
        }
    }

    // Abertura de Caixa
    async function handleOpenCashier() {
        if (!openOperatorName.trim()) {
            onNotify('Informe o nome do operador do caixa.', 'warning');
            return;
        }
        try {
            await openCashier(openOperatorName, openInitialCash, openNotes);
            showOpenCashierModal = false;
            onNotify(`Caixa aberto para ${openOperatorName} com fundo de R$ ${parseFloat(openInitialCash || 0).toFixed(2)}`, 'success');
            await loadCashier();
        } catch (err) {
            onNotify(err.message, 'error');
        }
    }

    // Movimentação de Caixa (Sangria / Suprimento)
    async function handleAddMovement() {
        const amt = parseFloat(movementAmount);
        if (isNaN(amt) || amt <= 0) {
            onNotify('Informe um valor válido maior que zero.', 'warning');
            return;
        }
        if (!movementReason.trim()) {
            onNotify('Informe o motivo ou justificativa.', 'warning');
            return;
        }
        try {
            await addCashierMovement(movementType, amt, movementReason);
            showMovementModal = false;
            movementAmount = '';
            movementReason = '';
            onNotify(`${movementType === 'sangria' ? 'Sangria' : 'Suprimento'} de R$ ${amt.toFixed(2)} registrado com sucesso.`, 'success');
            await loadCashier();
        } catch (err) {
            onNotify(err.message, 'error');
        }
    }

    // Fechamento de Caixa
    async function handleCloseCashier() {
        const counted = parseFloat(countedCash);
        if (isNaN(counted) || counted < 0) {
            onNotify('Informe o valor em dinheiro contado na gaveta.', 'warning');
            return;
        }
        try {
            const res = await closeCashier(counted, closeNotes);
            showCloseCashierModal = false;
            countedCash = '';
            closeNotes = '';
            
            const diff = res.audit?.difference || 0;
            let diffMsg = 'Caixa conferido sem divergência.';
            if (diff > 0.01) diffMsg = `Sobra de caixa: +R$ ${diff.toFixed(2)}`;
            if (diff < -0.01) diffMsg = `Falta de caixa: -R$ ${Math.abs(diff).toFixed(2)}`;

            onNotify(`Caixa finalizado com sucesso. ${diffMsg}`, 'success');
            await loadCashier();
        } catch (err) {
            onNotify(err.message, 'error');
        }
    }

    // Impressão Térmica de Fechamento de Caixa
    async function handlePrintCashierReceipt(shiftId) {
        if (!shiftId) {
            onNotify('Nenhum turno selecionado para impressão.', 'warning');
            return;
        }

        const selectedPrinter = localStorage.getItem("matafome_selected_printer") || "";
        const silentMode = localStorage.getItem("matafome_silent_print") !== "false";

        // Modo A: Impressão Nativa Silenciosa no Electron (App Desktop)
        if (typeof window !== "undefined" && window.desktop?.printSilent && silentMode) {
            try {
                onNotify('Imprimindo comprovante de fechamento na impressora térmica...', 'info');
                const silentUrl = getCashierReceiptUrl(shiftId, { width: 80, auto: false });
                const res = await window.desktop.printSilent({
                    url: silentUrl,
                    deviceName: selectedPrinter
                });
                if (res && res.success) {
                    onNotify('Comprovante de fechamento impresso com sucesso!', 'success');
                    return;
                } else {
                    console.warn('[Cashier Print] Falha silenciosa no Electron:', res?.failureReason);
                    onNotify(`Aviso de impressão: ${res?.failureReason || 'Tentando modo padrão...'}`, 'warning');
                }
            } catch (err) {
                console.warn('[Cashier Print] Erro no Electron silent print:', err);
            }
        }

        // Modo B: Contingência Web (Navegador com diálogo / popup com auto=true e token)
        try {
            const webUrl = getCashierReceiptUrl(shiftId, { width: 80, auto: true });
            const printWin = window.open(webUrl, `caixa_${shiftId}`, 'width=420,height=680,menubar=no,toolbar=no,location=no,status=no');
            if (!printWin) {
                let iframe = document.getElementById('thermal-cashier-frame');
                if (!iframe) {
                    iframe = document.createElement('iframe');
                    iframe.id = 'thermal-cashier-frame';
                    iframe.style.display = 'none';
                    document.body.appendChild(iframe);
                }
                iframe.src = webUrl;
            }
            onNotify('Enviando comprovante de fechamento para a impressora...', 'success');
        } catch (err) {
            onNotify(`Erro ao abrir comprovante de impressão: ${err.message}`, 'error');
        }
    }

    function formatBRL(val) {
        return 'R$ ' + parseFloat(val || 0).toFixed(2).replace('.', ',');
    }
</script>

<div class="cashier-container">
    <!-- Header Profissional do Caixa & PDV -->
    <header class="cashier-header">
        <div class="header-titles">
            <div class="header-badge-row">
                <div class="header-badge">
                    <Icon name="wallet" size={13} />
                    <span>Módulo Financeiro & Turno</span>
                </div>
                <div class="shift-status-indicator" class:open={cashierData.isBoxOpen} class:closed={!cashierData.isBoxOpen}>
                    <span class="status-pulse-dot"></span>
                    <span>{cashierData.isBoxOpen ? 'Caixa Aberto' : 'Caixa Fechado'}</span>
                </div>
            </div>
            <h1>Caixa & PDV</h1>
            <p class="subtitle">Controle financeiro e operações do caixa</p>
        </div>

        <div class="header-actions">
            <button class="btn-refresh" on:click={loadCashier} title="Atualizar dados do caixa" disabled={isLoading}>
                <span class:spin={isLoading}><Icon name="refresh" size={14} /></span>
                <span>Atualizar</span>
            </button>
            {#if !cashierData.isBoxOpen}
                <button class="btn-primary btn-header-open" on:click={() => showOpenCashierModal = true}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    <span>Abrir Caixa</span>
                </button>
            {/if}
        </div>
    </header>

    <div class="cashier-content">
        <!-- Banner / Hero de Estado Operacional do Turno -->
        <section class="cashier-hero-card" class:is-open={cashierData.isBoxOpen} class:is-closed={!cashierData.isBoxOpen}>
            <div class="hero-main-info">
                <div class="hero-status-icon-wrap" class:open={cashierData.isBoxOpen} class:closed={!cashierData.isBoxOpen}>
                    {#if cashierData.isBoxOpen}
                        <Icon name="check-circle" size={24} />
                    {:else}
                        <Icon name="lock" size={24} />
                    {/if}
                </div>
                <div class="hero-text-block">
                    <div class="hero-headline-row">
                        <h2>{cashierData.isBoxOpen ? 'Turno de Caixa em Andamento' : 'Caixa Fechado no Momento'}</h2>
                        <span class="hero-badge" class:open={cashierData.isBoxOpen} class:closed={!cashierData.isBoxOpen}>
                            {cashierData.isBoxOpen ? 'Operando' : 'Encerrado'}
                        </span>
                    </div>

                    {#if cashierData.isBoxOpen}
                        <div class="hero-meta-chips">
                            <span class="meta-chip">
                                <Icon name="user" size={12} />
                                <strong>Operador:</strong> {cashierData.shift?.operator_name || 'Operador'}
                            </span>
                            <span class="meta-chip">
                                <Icon name="clock" size={12} />
                                <strong>Abertura:</strong> {new Date(cashierData.shift?.opened_at).toLocaleTimeString('pt-BR').slice(0, 5)}
                            </span>
                            <span class="meta-chip">
                                <Icon name="wallet" size={12} />
                                <strong>Fundo Inicial:</strong> {formatBRL(cashierData.shift?.initial_cash)}
                            </span>
                            <span class="meta-chip">
                                <Icon name="shopping-bag" size={12} />
                                <strong>Vendas no Turno:</strong> {cashierData.summary?.ordersCount || 0} pedidos
                            </span>
                        </div>
                    {:else}
                        <p class="hero-closed-desc">
                            Abra um novo turno de expediente para registrar vendas em dinheiro, aportes de troco, sangrias de segurança e fechar a gaveta ao final do expediente.
                        </p>
                        {#if cashierData.lastClosedShift}
                            <div class="last-shift-summary">
                                <Icon name="clock" size={13} />
                                <span>Último fechamento: <strong>{new Date(cashierData.lastClosedShift.closed_at || cashierData.lastClosedShift.opened_at).toLocaleString('pt-BR')}</strong> por <strong>{cashierData.lastClosedShift.operator_name}</strong></span>
                            </div>
                        {/if}
                    {/if}
                </div>
            </div>

            <!-- Ações Rápidas do Banner -->
            <div class="hero-actions-cluster">
                {#if cashierData.isBoxOpen}
                    <button class="quick-btn sangria" on:click={() => { movementType = 'sangria'; showMovementModal = true; }} title="Realizar sangria (retirada de dinheiro da gaveta)">
                        <span class="quick-icon-wrap sangria-icon">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                        </span>
                        <div class="quick-btn-labels">
                            <span class="quick-title">Sangria</span>
                            <span class="quick-sub">Retirada</span>
                        </div>
                    </button>

                    <button class="quick-btn suprimento" on:click={() => { movementType = 'suprimento'; showMovementModal = true; }} title="Realizar suprimento (aporte de troco na gaveta)">
                        <span class="quick-icon-wrap suprimento-icon">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                        </span>
                        <div class="quick-btn-labels">
                            <span class="quick-title">Suprimento</span>
                            <span class="quick-sub">Entrada de Troco</span>
                        </div>
                    </button>

                    <button class="quick-btn print" on:click={() => handlePrintCashierReceipt(cashierData.shift?.id)} title="Imprimir comprovante parcial (80mm)">
                        <span class="quick-icon-wrap print-icon">
                            <Icon name="printer" size={15} />
                        </span>
                        <div class="quick-btn-labels">
                            <span class="quick-title">Imprimir</span>
                            <span class="quick-sub">Parcial 80mm</span>
                        </div>
                    </button>

                    <button class="quick-btn close-box" on:click={() => showCloseCashierModal = true} title="Encerrar e conferir o caixa">
                        <span class="quick-icon-wrap close-icon">
                            <Icon name="lock" size={14} />
                        </span>
                        <div class="quick-btn-labels">
                            <span class="quick-title">Fechar Caixa</span>
                            <span class="quick-sub">Conferência</span>
                        </div>
                    </button>
                {:else}
                    <button class="btn-primary hero-btn-open" on:click={() => showOpenCashierModal = true}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                        <span>Abrir Turno de Caixa</span>
                    </button>
                    {#if cashierData.lastClosedShift}
                        <button class="btn-outline-print" on:click={() => handlePrintCashierReceipt(cashierData.lastClosedShift.id)}>
                            <Icon name="printer" size={15} />
                            <span>Imprimir Último Fechamento</span>
                        </button>
                    {/if}
                {/if}
            </div>
        </section>

        {#if cashierData.isBoxOpen}
            <!-- Resumo Financeiro / Faixa de Indicadores (KPIs) -->
            <section class="cashier-kpi-bar" aria-label="Resumo Financeiro do Caixa">
                <!-- Saldo Esperado na Gaveta (Card Destacado) -->
                <div class="kpi-card highlight-drawer">
                    <div class="kpi-card-header">
                        <span class="kpi-tag-badge">Físico em Espécie</span>
                        <Icon name="wallet" size={15} />
                    </div>
                    <span class="kpi-label">Saldo Esperado na Gaveta</span>
                    <span class="kpi-val drawer-val">{formatBRL(cashierData.summary.expectedCash)}</span>
                    <span class="kpi-sub">Valor físico previsto na gaveta</span>
                </div>

                <div class="kpi-card">
                    <div class="kpi-card-header">
                        <span class="kpi-label">Fundo Inicial</span>
                        <span class="kpi-mini-icon"><Icon name="dollar-sign" size={13} /></span>
                    </div>
                    <span class="kpi-val">{formatBRL(cashierData.summary.initialCash)}</span>
                    <span class="kpi-sub">Fundo colocado na abertura</span>
                </div>

                <div class="kpi-card">
                    <div class="kpi-card-header">
                        <span class="kpi-label">(+) Vendas em Dinheiro</span>
                        <span class="kpi-mini-icon positive"><Icon name="check" size={13} /></span>
                    </div>
                    <span class="kpi-val positive">+ {formatBRL(cashierData.summary.cashSales)}</span>
                    <span class="kpi-sub">{cashierData.summary.ordersCount} pedidos no turno</span>
                </div>

                <div class="kpi-card">
                    <div class="kpi-card-header">
                        <span class="kpi-label">(+) Suprimentos</span>
                        <span class="kpi-mini-icon blue"><Icon name="clock" size={13} /></span>
                    </div>
                    <span class="kpi-val positive">+ {formatBRL(cashierData.summary.supplements)}</span>
                    <span class="kpi-sub">Aportes extras de troco</span>
                </div>

                <div class="kpi-card">
                    <div class="kpi-card-header">
                        <span class="kpi-label">(-) Sangrias</span>
                        <span class="kpi-mini-icon negative"><Icon name="x" size={13} /></span>
                    </div>
                    <span class="kpi-val negative">- {formatBRL(cashierData.summary.sangrias)}</span>
                    <span class="kpi-sub">Retiradas de segurança</span>
                </div>

                <div class="kpi-card">
                    <div class="kpi-card-header">
                        <span class="kpi-label">Vendas no PIX</span>
                        <span class="kpi-mini-icon pix"><Icon name="zap" size={13} /></span>
                    </div>
                    <span class="kpi-val">{formatBRL(cashierData.summary.pixSales)}</span>
                    <span class="kpi-sub">Conta bancária digital</span>
                </div>

                <div class="kpi-card">
                    <div class="kpi-card-header">
                        <span class="kpi-label">Vendas no Cartão</span>
                        <span class="kpi-mini-icon card"><Icon name="credit-card" size={13} /></span>
                    </div>
                    <span class="kpi-val">{formatBRL(cashierData.summary.cardSales)}</span>
                    <span class="kpi-sub">Crédito & Débito maquininha</span>
                </div>

                <div class="kpi-card highlight-revenue">
                    <div class="kpi-card-header">
                        <span class="kpi-label">Faturamento Total</span>
                        <span class="kpi-mini-icon brand"><Icon name="shopping-bag" size={13} /></span>
                    </div>
                    <span class="kpi-val revenue-val">{formatBRL(cashierData.summary.totalRevenue)}</span>
                    <span class="kpi-sub">Receita bruta do turno</span>
                </div>
            </section>

            <!-- Tabela de Movimentações da Sessão (Sangrias e Suprimentos) -->
            <section class="card movements-card" aria-label="Histórico de Movimentações">
                <div class="card-header">
                    <div class="card-title">
                        <div class="title-icon-wrap">
                            <Icon name="file-text" size={16} />
                        </div>
                        <div>
                            <h3>Movimentações do Turno ({cashierData.movements?.length || 0})</h3>
                            <p class="tag-hint">Histórico auditável de sangrias e suprimentos de caixa</p>
                        </div>
                    </div>
                    <div class="card-actions">
                        <button class="btn-action-small sangria" on:click={() => { movementType = 'sangria'; showMovementModal = true; }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                            <span>Nova Sangria</span>
                        </button>
                        <button class="btn-action-small suprimento" on:click={() => { movementType = 'suprimento'; showMovementModal = true; }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                            <span>Novo Suprimento</span>
                        </button>
                    </div>
                </div>

                <div class="table-responsive">
                    <table class="mov-table">
                        <thead>
                            <tr>
                                <th>Horário</th>
                                <th>Tipo</th>
                                <th>Motivo / Justificativa</th>
                                <th style="text-align: right;">Valor</th>
                            </tr>
                        </thead>
                        <tbody>
                            {#if !cashierData.movements || cashierData.movements.length === 0}
                                <tr>
                                    <td colspan="4">
                                        <div class="empty-movements-block">
                                            <div class="empty-icon-subtle"><Icon name="file-text" size={24} /></div>
                                            <p>Nenhuma sangria ou suprimento registrado neste turno.</p>
                                            <small>Utilize os botões acima quando precisar retirar dinheiro para o cofre ou abastecer a gaveta com troco.</small>
                                        </div>
                                    </td>
                                </tr>
                            {:else}
                                {#each cashierData.movements as m}
                                    <tr class="mov-row {m.type}">
                                        <td class="time-td">
                                            <span class="time-badge">
                                                <Icon name="clock" size={11} />
                                                {new Date(m.created_at).toLocaleTimeString('pt-BR').slice(0, 5)}
                                            </span>
                                        </td>
                                        <td>
                                            <span class="tag-type {m.type}">
                                                {#if m.type === 'sangria'}
                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                                                    <span>Sangria (-)</span>
                                                {:else}
                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                                                    <span>Suprimento (+)</span>
                                                {/if}
                                            </span>
                                        </td>
                                        <td class="reason-td">
                                            <span class="reason-text">{m.reason}</span>
                                        </td>
                                        <td class="amount-td {m.type}">
                                            <strong>{m.type === 'sangria' ? '-' : '+'} {formatBRL(m.amount)}</strong>
                                        </td>
                                    </tr>
                                {/each}
                            {/if}
                        </tbody>
                    </table>
                </div>
            </section>
        {:else}
            <!-- Estado Quando Caixa Está Fechado -->
            <div class="empty-cashier-card">
                <div class="empty-icon-box">
                    <Icon name="lock" size={32} />
                </div>
                <h3>O Caixa está Fechado no Momento</h3>
                <p>Abra o caixa no início do expediente para acompanhar a gaveta física, registrar fundo de troco, sangrias de segurança e reconciliar as vendas do restaurante.</p>
                <div class="empty-card-actions">
                    <button class="btn-primary btn-large-open" on:click={() => showOpenCashierModal = true}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                        <span>Abrir Turno de Caixa Agora</span>
                    </button>
                    {#if cashierData.lastClosedShift}
                        <button class="btn-secondary-action" on:click={() => handlePrintCashierReceipt(cashierData.lastClosedShift.id)}>
                            <Icon name="printer" size={15} />
                            <span>Imprimir Comprovante do Último Turno</span>
                        </button>
                    {/if}
                </div>
            </div>
        {/if}
    </div>

    <!-- MODAL 1: ABERTURA DE CAIXA -->
    {#if showOpenCashierModal}
        <div class="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="modal-open-title">
            <div class="modal-card">
                <div class="modal-header">
                    <div class="modal-title-wrap">
                        <span class="modal-pretitle">Gestão de Expediente</span>
                        <h3 id="modal-open-title">Abertura de Turno de Caixa</h3>
                    </div>
                    <button class="btn-close-modal" on:click={() => showOpenCashierModal = false} aria-label="Fechar modal"><Icon name="x" size={16} /></button>
                </div>
                <div class="modal-body">
                    <div class="form-group">
                        <label for="open-operator-name">Nome do Operador *</label>
                        <input
                            id="open-operator-name"
                            type="text"
                            bind:value={openOperatorName}
                            placeholder="Ex: Carlos Caixa, Maria Silva"
                        />
                    </div>
                    <div class="form-group">
                        <label for="open-initial-cash">Fundo de Troco Inicial (R$)</label>
                        <input
                            id="open-initial-cash"
                            type="number"
                            step="0.01"
                            bind:value={openInitialCash}
                            placeholder="100.00"
                        />
                        <small>Valor em notas e moedas colocado na gaveta na abertura.</small>
                    </div>
                    <div class="form-group">
                        <label for="open-notes">Observações do Turno</label>
                        <input
                            id="open-notes"
                            type="text"
                            bind:value={openNotes}
                            placeholder="Ex: Turno da noite, troca de equipe"
                        />
                    </div>
                </div>
                <div class="modal-footer">
                    <button class="btn-cancel" on:click={() => showOpenCashierModal = false}>Cancelar</button>
                    <button class="btn-primary" on:click={handleOpenCashier}>
                        <Icon name="check" size={14} />
                        <span>Confirmar Abertura</span>
                    </button>
                </div>
            </div>
        </div>
    {/if}

    <!-- MODAL 2: SANGRIA / SUPRIMENTO -->
    {#if showMovementModal}
        <div class="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="modal-mov-title">
            <div class="modal-card">
                <div class="modal-header">
                    <div class="modal-title-wrap">
                        <span class="modal-pretitle">Movimentação Financeira</span>
                        <h3 id="modal-mov-title">{movementType === 'sangria' ? 'Registrar Sangria (Retirada)' : 'Registrar Suprimento (Aporte de Troco)'}</h3>
                    </div>
                    <button class="btn-close-modal" on:click={() => showMovementModal = false} aria-label="Fechar modal"><Icon name="x" size={16} /></button>
                </div>
                <div class="modal-body">
                    <div class="movement-type-indicator {movementType}">
                        {#if movementType === 'sangria'}
                            <div class="mov-indicator-icon sangria">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                            </div>
                            <div>
                                <strong>Retirada de Dinheiro da Gaveta</strong>
                                <p>Este valor será subtraído do saldo esperado em dinheiro na conferência final.</p>
                            </div>
                        {:else}
                            <div class="mov-indicator-icon suprimento">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                            </div>
                            <div>
                                <strong>Entrada de Troco na Gaveta</strong>
                                <p>Este valor será somado ao saldo esperado em dinheiro na conferência final.</p>
                            </div>
                        {/if}
                    </div>

                    <div class="form-group">
                        <label for="mov-amount">Valor da Movimentação (R$) *</label>
                        <input
                            id="mov-amount"
                            type="number"
                            step="0.01"
                            bind:value={movementAmount}
                            placeholder="0.00"
                        />
                    </div>
                    <div class="form-group">
                        <label for="mov-reason">Motivo ou Justificativa *</label>
                        <input
                            id="mov-reason"
                            type="text"
                            bind:value={movementReason}
                            placeholder={movementType === 'sangria' ? 'Ex: Sangria para o cofre, pagamento de motoboy' : 'Ex: Troco de moedas, reforço de notas de R$ 5'}
                        />
                    </div>
                </div>
                <div class="modal-footer">
                    <button class="btn-cancel" on:click={() => showMovementModal = false}>Cancelar</button>
                    <button class="btn-confirm {movementType === 'sangria' ? 'orange' : 'blue'}" on:click={handleAddMovement}>
                        <Icon name="check" size={14} />
                        <span>Registrar {movementType === 'sangria' ? 'Sangria' : 'Suprimento'}</span>
                    </button>
                </div>
            </div>
        </div>
    {/if}

    <!-- MODAL 3: FECHAMENTO DE CAIXA -->
    {#if showCloseCashierModal}
        <div class="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="modal-close-title">
            <div class="modal-card modal-card-close">
                <div class="modal-header">
                    <div class="modal-title-wrap">
                        <span class="modal-pretitle">Encerramento de Expediente</span>
                        <h3 id="modal-close-title">Fechamento de Caixa & Conferência</h3>
                    </div>
                    <button class="btn-close-modal" on:click={() => showCloseCashierModal = false} aria-label="Fechar modal"><Icon name="x" size={16} /></button>
                </div>
                <div class="modal-body">
                    <div class="info-alert-box">
                        <div class="info-row-box">
                            <span>Saldo Esperado na Gaveta:</span>
                            <strong class="highlight-expected">{formatBRL(cashierData.summary.expectedCash)}</strong>
                        </div>
                        <div class="info-row-box">
                            <span>Total de Vendas do Turno:</span>
                            <span>{formatBRL(cashierData.summary.totalRevenue)}</span>
                        </div>
                    </div>

                    <div class="form-group">
                        <label for="close-counted-cash">Valor em Dinheiro Contado na Gaveta (R$) *</label>
                        <input
                            id="close-counted-cash"
                            type="number"
                            step="0.01"
                            bind:value={countedCash}
                            placeholder="Digite o valor físico contado"
                        />
                    </div>

                    {#if countedCash !== ''}
                        {@const diff = parseFloat(countedCash || 0) - cashierData.summary.expectedCash}
                        <div class="diff-preview-box {Math.abs(diff) < 0.01 ? 'exact' : (diff > 0 ? 'surplus' : 'deficit')}">
                            {#if Math.abs(diff) < 0.01}
                                <Icon name="check" size={14} />
                                <span>Caixa conferido sem divergência (R$ 0,00)</span>
                            {:else if diff > 0}
                                <Icon name="alert-triangle" size={14} />
                                <span>Sobra de Caixa: +{formatBRL(diff)}</span>
                            {:else}
                                <Icon name="alert-triangle" size={14} />
                                <span>Falta de Caixa: -{formatBRL(Math.abs(diff))}</span>
                            {/if}
                        </div>
                    {/if}

                    <div class="form-group" style="margin-top: 12px;">
                        <label for="close-notes">Observações do Fechamento</label>
                        <input
                            id="close-notes"
                            type="text"
                            bind:value={closeNotes}
                            placeholder="Ex: Tudo correto, dinheiro guardado no cofre"
                        />
                    </div>
                </div>
                <div class="modal-footer">
                    <button class="btn-cancel" on:click={() => showCloseCashierModal = false}>Cancelar</button>
                    <button class="btn-confirm red" on:click={handleCloseCashier}>
                        <Icon name="lock" size={14} />
                        <span>Finalizar & Fechar Caixa</span>
                    </button>
                </div>
            </div>
        </div>
    {/if}
</div>

<style>
    .cashier-container {
        display: flex;
        flex-direction: column;
        min-height: 100%;
        box-sizing: border-box;
        background: var(--neutral-bg, #F8FAFC);
        overflow-y: auto;
        padding: 24px;
        color: var(--text-primary, #0F172A);
        font-family: inherit;
    }

    /* HEADER */
    .cashier-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
        flex-wrap: wrap;
        gap: 16px;
    }

    .header-titles {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .header-badge-row {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 2px;
    }

    .header-badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: var(--brand-primary-soft, #FFF7ED);
        color: var(--brand-primary, #FF6A00);
        border: 1px solid rgba(255, 106, 0, 0.2);
        padding: 4px 10px;
        border-radius: var(--radius-full, 9999px);
        font-size: 11.5px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    .shift-status-indicator {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 3px 10px;
        border-radius: var(--radius-full, 9999px);
        font-size: 11.5px;
        font-weight: 700;
    }

    .shift-status-indicator.open {
        background: var(--semantic-success-soft, #ECFDF5);
        color: var(--semantic-success, #059669);
        border: 1px solid rgba(5, 150, 105, 0.25);
    }

    .shift-status-indicator.closed {
        background: var(--semantic-danger-soft, #FEF2F2);
        color: var(--semantic-danger, #DC2626);
        border: 1px solid rgba(220, 38, 38, 0.25);
    }

    .status-pulse-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: currentColor;
    }

    .shift-status-indicator.open .status-pulse-dot {
        box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.25);
        animation: pulseLive 2s infinite ease-in-out;
    }

    @keyframes pulseLive {
        0%, 100% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.3); opacity: 0.6; }
    }

    .cashier-header h1 {
        font-size: 24px;
        font-weight: 900;
        margin: 0;
        color: var(--text-primary, #0F172A);
        letter-spacing: -0.5px;
    }

    .subtitle {
        font-size: 13px;
        color: var(--text-secondary, #475569);
        margin: 0;
    }

    .header-actions {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .btn-refresh {
        background: var(--neutral-surface, #FFFFFF);
        border: 1px solid var(--neutral-border, #CBD5E1);
        padding: 9px 14px;
        border-radius: var(--radius-md, 10px);
        font-size: 13px;
        font-weight: 600;
        color: var(--text-secondary, #475569);
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        transition: all 0.15s ease;
    }

    .btn-refresh:hover:not(:disabled) {
        background: var(--neutral-surface-hover, #F1F5F9);
        color: var(--text-primary, #0F172A);
        border-color: var(--neutral-border-focus, #94A3B8);
    }

    .btn-refresh:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .btn-primary {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        background: var(--brand-primary, #FF6A00);
        color: #FFFFFF;
        border: none;
        padding: 9px 18px;
        border-radius: var(--radius-md, 10px);
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.15s ease;
        box-shadow: 0 2px 4px rgba(255, 106, 0, 0.2);
    }

    .btn-primary:hover {
        background: var(--brand-primary-hover, #E85A00);
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(255, 106, 0, 0.25);
    }

    .btn-primary:active {
        transform: translateY(0);
    }

    /* CASHIER CONTENT */
    .cashier-content {
        display: flex;
        flex-direction: column;
        gap: 20px;
        flex: 1;
    }

    /* HERO OPERACIONAL CARD */
    .cashier-hero-card {
        background: var(--neutral-surface, #FFFFFF);
        border-radius: var(--radius-lg, 16px);
        padding: 22px 24px;
        border: 1px solid var(--neutral-border, #E2E8F0);
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;
        gap: 20px;
        box-shadow: var(--shadow-sm, 0 1px 3px rgba(0,0,0,0.04));
        transition: all 0.2s ease;
    }

    .cashier-hero-card.is-open {
        border-left: 4px solid var(--semantic-success, #059669);
    }

    .cashier-hero-card.is-closed {
        border-left: 4px solid var(--semantic-danger, #DC2626);
    }

    .hero-main-info {
        display: flex;
        align-items: flex-start;
        gap: 16px;
        flex: 1;
        min-width: 280px;
    }

    .hero-status-icon-wrap {
        width: 48px;
        height: 48px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .hero-status-icon-wrap.open {
        background: var(--semantic-success-soft, #ECFDF5);
        color: var(--semantic-success, #059669);
    }

    .hero-status-icon-wrap.closed {
        background: var(--semantic-danger-soft, #FEF2F2);
        color: var(--semantic-danger, #DC2626);
    }

    .hero-text-block {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    .hero-headline-row {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;
    }

    .hero-headline-row h2 {
        margin: 0;
        font-size: 18px;
        font-weight: 800;
        color: var(--text-primary, #0F172A);
    }

    .hero-badge {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        padding: 2px 8px;
        border-radius: 6px;
    }

    .hero-badge.open {
        background: var(--semantic-success-soft, #ECFDF5);
        color: var(--semantic-success, #059669);
    }

    .hero-badge.closed {
        background: var(--semantic-danger-soft, #FEF2F2);
        color: var(--semantic-danger, #DC2626);
    }

    .hero-meta-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        margin-top: 4px;
    }

    .meta-chip {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        background: var(--neutral-surface-hover, #F1F5F9);
        color: var(--text-secondary, #475569);
        border: 1px solid var(--neutral-border, #E2E8F0);
        padding: 4px 10px;
        border-radius: 8px;
        font-size: 12px;
    }

    .meta-chip strong {
        color: var(--text-primary, #0F172A);
    }

    .hero-closed-desc {
        margin: 0;
        font-size: 13px;
        color: var(--text-secondary, #64748B);
        max-width: 600px;
        line-height: 1.4;
    }

    .last-shift-summary {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin-top: 4px;
        font-size: 12px;
        color: var(--text-secondary, #64748B);
    }

    .hero-actions-cluster {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
        align-items: center;
    }

    .quick-btn {
        display: flex;
        align-items: center;
        gap: 8px;
        background: var(--neutral-surface, #FFFFFF);
        border: 1px solid var(--neutral-border, #CBD5E1);
        padding: 8px 12px;
        border-radius: var(--radius-md, 10px);
        cursor: pointer;
        transition: all 0.15s ease;
        text-align: left;
    }

    .quick-btn:hover {
        background: var(--neutral-surface-hover, #F8FAFC);
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    }

    .quick-btn:active {
        transform: translateY(0);
    }

    .quick-icon-wrap {
        width: 28px;
        height: 28px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .quick-icon-wrap.sangria-icon {
        background: #FFF7ED;
        color: #EA580C;
    }

    .quick-icon-wrap.suprimento-icon {
        background: #EFF6FF;
        color: #2563EB;
    }

    .quick-icon-wrap.print-icon {
        background: #F1F5F9;
        color: #475569;
    }

    .quick-icon-wrap.close-icon {
        background: #FEF2F2;
        color: #DC2626;
    }

    .quick-btn-labels {
        display: flex;
        flex-direction: column;
    }

    .quick-title {
        font-size: 12px;
        font-weight: 700;
        color: var(--text-primary, #0F172A);
    }

    .quick-sub {
        font-size: 10.5px;
        color: var(--text-muted, #94A3B8);
    }

    .quick-btn.sangria:hover { border-color: #FDBA74; }
    .quick-btn.suprimento:hover { border-color: #93C5FD; }
    .quick-btn.print:hover { border-color: #CBD5E1; }
    .quick-btn.close-box:hover { border-color: #FCA5A5; background: #FFF5F5; }

    .btn-outline-print {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: #FFFFFF;
        border: 1px solid var(--neutral-border, #CBD5E1);
        padding: 9px 14px;
        border-radius: var(--radius-md, 10px);
        font-size: 13px;
        font-weight: 600;
        color: var(--text-secondary, #475569);
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .btn-outline-print:hover {
        background: var(--neutral-surface-hover, #F1F5F9);
        color: var(--text-primary, #0F172A);
    }

    /* KPI BAR */
    .cashier-kpi-bar {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 14px;
    }

    .kpi-card {
        background: var(--neutral-surface, #FFFFFF);
        border: 1px solid var(--neutral-border, #E2E8F0);
        padding: 16px 18px;
        border-radius: var(--radius-md, 12px);
        display: flex;
        flex-direction: column;
        transition: all 0.15s ease;
        box-shadow: var(--shadow-sm, 0 1px 2px rgba(0,0,0,0.03));
    }

    .kpi-card:hover {
        box-shadow: 0 3px 6px rgba(0,0,0,0.06);
        border-color: var(--neutral-border-focus, #CBD5E1);
    }

    .kpi-card.highlight-drawer {
        background: #FFFBEB;
        border-color: #FDE68A;
        grid-column: span 1;
    }

    .kpi-card.highlight-revenue {
        background: var(--brand-primary-soft, #FFF7ED);
        border-color: rgba(255, 106, 0, 0.25);
    }

    .kpi-card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 6px;
    }

    .kpi-tag-badge {
        font-size: 10px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        background: #F59E0B;
        color: #FFFFFF;
        padding: 2px 6px;
        border-radius: 4px;
    }

    .kpi-mini-icon {
        width: 22px;
        height: 22px;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--neutral-surface-hover, #F1F5F9);
        color: var(--text-muted, #94A3B8);
    }

    .kpi-mini-icon.positive { background: #ECFDF5; color: #059669; }
    .kpi-mini-icon.negative { background: #FEF2F2; color: #DC2626; }
    .kpi-mini-icon.blue { background: #EFF6FF; color: #2563EB; }
    .kpi-mini-icon.pix { background: #F0FDF4; color: #16A34A; }
    .kpi-mini-icon.card { background: #FAF5FF; color: #9333EA; }
    .kpi-mini-icon.brand { background: #FFF7ED; color: #EA580C; }

    .kpi-label {
        font-size: 11.5px;
        font-weight: 700;
        color: var(--text-secondary, #64748B);
        text-transform: uppercase;
        letter-spacing: 0.3px;
        margin-bottom: 4px;
    }

    .kpi-val {
        font-size: 20px;
        font-weight: 900;
        color: var(--text-primary, #0F172A);
        margin-bottom: 2px;
    }

    .kpi-val.positive { color: var(--semantic-success, #059669); }
    .kpi-val.negative { color: var(--semantic-danger, #DC2626); }
    .kpi-val.drawer-val { color: #B45309; font-size: 22px; }
    .kpi-val.revenue-val { color: var(--brand-primary, #FF6A00); font-size: 22px; }

    .kpi-sub {
        font-size: 11px;
        color: var(--text-muted, #94A3B8);
    }

    /* CARD MOVIMENTAÇÕES */
    .card {
        background: var(--neutral-surface, #FFFFFF);
        border: 1px solid var(--neutral-border, #E2E8F0);
        border-radius: var(--radius-lg, 16px);
        padding: 22px 24px;
        box-shadow: var(--shadow-sm, 0 1px 3px rgba(0,0,0,0.04));
    }

    .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 18px;
        flex-wrap: wrap;
        gap: 12px;
    }

    .card-title {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .title-icon-wrap {
        width: 36px;
        height: 36px;
        border-radius: 10px;
        background: var(--neutral-surface-hover, #F1F5F9);
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--text-secondary, #475569);
    }

    .card-title h3 {
        margin: 0;
        font-size: 16px;
        font-weight: 800;
        color: var(--text-primary, #0F172A);
    }

    .tag-hint {
        font-size: 12px;
        color: var(--text-secondary, #64748B);
        margin: 2px 0 0 0;
    }

    .card-actions {
        display: flex;
        gap: 8px;
    }

    .btn-action-small {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 6px 12px;
        border-radius: var(--radius-md, 8px);
        font-size: 12px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.15s ease;
        border: 1px solid transparent;
    }

    .btn-action-small.sangria {
        background: #FFF7ED;
        color: #EA580C;
        border-color: #FFEDD5;
    }

    .btn-action-small.sangria:hover {
        background: #EA580C;
        color: #FFFFFF;
    }

    .btn-action-small.suprimento {
        background: #EFF6FF;
        color: #2563EB;
        border-color: #DBEAFE;
    }

    .btn-action-small.suprimento:hover {
        background: #2563EB;
        color: #FFFFFF;
    }

    .table-responsive {
        width: 100%;
        overflow-x: auto;
    }

    .mov-table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        font-size: 13px;
    }

    .mov-table th {
        text-align: left;
        color: var(--text-secondary, #64748B);
        font-size: 11.5px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        padding: 10px 14px;
        background: var(--neutral-surface-hover, #F8FAFC);
        border-bottom: 1px solid var(--neutral-border, #E2E8F0);
    }

    .mov-table th:first-child {
        border-top-left-radius: 8px;
        border-bottom-left-radius: 8px;
    }

    .mov-table th:last-child {
        border-top-right-radius: 8px;
        border-bottom-right-radius: 8px;
    }

    .mov-table td {
        padding: 12px 14px;
        border-bottom: 1px solid var(--neutral-border, #F1F5F9);
        color: var(--text-primary, #0F172A);
        vertical-align: middle;
    }

    .mov-row:hover td {
        background: var(--neutral-surface-hover, #F8FAFC);
    }

    .time-badge {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        color: var(--text-secondary, #64748B);
        font-size: 12px;
        font-weight: 600;
    }

    .tag-type {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        font-size: 11.5px;
        font-weight: 700;
        padding: 3px 8px;
        border-radius: 6px;
    }

    .tag-type.sangria {
        background: var(--semantic-danger-soft, #FEF2F2);
        color: var(--semantic-danger, #DC2626);
        border: 1px solid rgba(220, 38, 38, 0.2);
    }

    .tag-type.suprimento {
        background: var(--semantic-info-soft, #EFF6FF);
        color: var(--semantic-info, #2563EB);
        border: 1px solid rgba(37, 99, 235, 0.2);
    }

    .reason-td {
        color: var(--text-primary, #0F172A);
        font-weight: 500;
    }

    .amount-td.sangria {
        color: var(--semantic-danger, #DC2626);
        text-align: right;
    }

    .amount-td.suprimento {
        color: var(--semantic-info, #2563EB);
        text-align: right;
    }

    .empty-movements-block {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 36px 16px;
        text-align: center;
        gap: 6px;
    }

    .empty-icon-subtle {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        background: var(--neutral-surface-hover, #F1F5F9);
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--text-muted, #94A3B8);
        margin-bottom: 4px;
    }

    .empty-movements-block p {
        margin: 0;
        font-size: 14px;
        font-weight: 700;
        color: var(--text-primary, #0F172A);
    }

    .empty-movements-block small {
        font-size: 12px;
        color: var(--text-secondary, #64748B);
        max-width: 420px;
    }

    /* EMPTY CASHIER CARD */
    .empty-cashier-card {
        background: var(--neutral-surface, #FFFFFF);
        border: 1px dashed var(--neutral-border, #CBD5E1);
        padding: 48px 24px;
        border-radius: var(--radius-lg, 16px);
        text-align: center;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        box-shadow: var(--shadow-sm, 0 1px 3px rgba(0,0,0,0.03));
    }

    .empty-icon-box {
        width: 68px;
        height: 68px;
        border-radius: 16px;
        background: var(--neutral-surface-hover, #F1F5F9);
        color: var(--text-secondary, #475569);
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .empty-cashier-card h3 {
        margin: 0;
        font-size: 18px;
        font-weight: 800;
        color: var(--text-primary, #0F172A);
    }

    .empty-cashier-card p {
        margin: 0 0 8px 0;
        font-size: 13.5px;
        color: var(--text-secondary, #64748B);
        max-width: 520px;
        line-height: 1.5;
    }

    .empty-card-actions {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
        justify-content: center;
    }

    .btn-secondary-action {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: #FFFFFF;
        border: 1px solid var(--neutral-border, #CBD5E1);
        padding: 10px 18px;
        border-radius: var(--radius-md, 10px);
        font-size: 13px;
        font-weight: 600;
        color: var(--text-secondary, #475569);
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .btn-secondary-action:hover {
        background: var(--neutral-surface-hover, #F1F5F9);
        color: var(--text-primary, #0F172A);
    }

    /* MODAIS */
    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        height: 100dvh;
        background: rgba(15, 23, 42, 0.65);
        backdrop-filter: blur(4px);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 9999;
        padding: 16px;
    }

    .modal-card {
        background: var(--neutral-surface, #FFFFFF);
        border-radius: var(--radius-lg, 16px);
        width: 480px;
        max-width: 100%;
        box-shadow: var(--shadow-modal, 0 20px 25px -5px rgba(0,0,0,0.15));
        overflow: hidden;
        animation: modalScaleUp 0.18s ease-out;
        border: 1px solid var(--neutral-border, #E2E8F0);
    }

    @keyframes modalScaleUp {
        from { transform: scale(0.96); opacity: 0; }
        to { transform: scale(1); opacity: 1; }
    }

    .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 18px 22px;
        background: var(--neutral-surface, #FFFFFF);
        border-bottom: 1px solid var(--neutral-border, #E2E8F0);
    }

    .modal-title-wrap {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .modal-pretitle {
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: var(--brand-primary, #FF6A00);
    }

    .modal-header h3 {
        margin: 0;
        font-size: 16px;
        font-weight: 800;
        color: var(--text-primary, #0F172A);
    }

    .btn-close-modal {
        background: var(--neutral-surface-hover, #F1F5F9);
        border: none;
        color: var(--text-secondary, #64748B);
        width: 32px;
        height: 32px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .btn-close-modal:hover {
        background: #FEE2E2;
        color: #DC2626;
    }

    .modal-body {
        padding: 22px;
        display: flex;
        flex-direction: column;
        gap: 16px;
        max-height: calc(85dvh - 120px);
        overflow-y: auto;
    }

    .movement-type-indicator {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 12px 14px;
        border-radius: var(--radius-md, 10px);
        font-size: 12.5px;
    }

    .movement-type-indicator.sangria {
        background: #FFF7ED;
        border: 1px solid #FFEDD5;
        color: #9A3412;
    }

    .movement-type-indicator.suprimento {
        background: #EFF6FF;
        border: 1px solid #DBEAFE;
        color: #1E40AF;
    }

    .mov-indicator-icon {
        width: 30px;
        height: 30px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .mov-indicator-icon.sangria { background: #EA580C; color: #FFFFFF; }
    .mov-indicator-icon.suprimento { background: #2563EB; color: #FFFFFF; }

    .movement-type-indicator p {
        margin: 2px 0 0 0;
        font-size: 11.5px;
        opacity: 0.85;
    }

    .form-group {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    .form-group label {
        font-size: 12px;
        font-weight: 700;
        color: var(--text-primary, #0F172A);
    }

    .form-group input {
        border: 1px solid var(--neutral-border, #CBD5E1);
        padding: 10px 14px;
        border-radius: var(--radius-md, 10px);
        font-size: 13.5px;
        color: var(--text-primary, #0F172A);
        background: #FFFFFF;
        transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }

    .form-group input:focus {
        outline: none;
        border-color: var(--brand-primary, #FF6A00);
        box-shadow: 0 0 0 3px rgba(255, 106, 0, 0.15);
    }

    .form-group small {
        font-size: 11px;
        color: var(--text-muted, #94A3B8);
    }

    .info-alert-box {
        background: var(--neutral-surface-hover, #F8FAFC);
        border: 1px solid var(--neutral-border, #E2E8F0);
        padding: 14px 16px;
        border-radius: var(--radius-md, 10px);
        display: flex;
        flex-direction: column;
        gap: 8px;
        font-size: 13px;
    }

    .info-row-box {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .highlight-expected {
        font-size: 16px;
        font-weight: 800;
        color: #B45309;
    }

    .diff-preview-box {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 12px;
        border-radius: var(--radius-md, 10px);
        font-size: 13px;
        font-weight: 700;
        text-align: center;
    }

    .diff-preview-box.exact {
        background: var(--semantic-success-soft, #ECFDF5);
        color: var(--semantic-success, #059669);
        border: 1px solid #A7F3D0;
    }

    .diff-preview-box.surplus {
        background: #FFFBEB;
        color: #B45309;
        border: 1px solid #FDE68A;
    }

    .diff-preview-box.deficit {
        background: var(--semantic-danger-soft, #FEF2F2);
        color: var(--semantic-danger, #DC2626);
        border: 1px solid #FECACA;
    }

    .modal-footer {
        padding: 16px 22px;
        background: var(--neutral-surface-hover, #F8FAFC);
        border-top: 1px solid var(--neutral-border, #E2E8F0);
        display: flex;
        justify-content: flex-end;
        gap: 10px;
    }

    .btn-cancel {
        background: #FFFFFF;
        border: 1px solid var(--neutral-border, #CBD5E1);
        padding: 9px 16px;
        border-radius: var(--radius-md, 10px);
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        color: var(--text-secondary, #475569);
        transition: all 0.15s ease;
    }

    .btn-cancel:hover {
        background: #F1F5F9;
        color: #0F172A;
    }

    .btn-confirm {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        border: none;
        color: #FFFFFF;
        padding: 9px 18px;
        border-radius: var(--radius-md, 10px);
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .btn-confirm:hover {
        transform: translateY(-1px);
        opacity: 0.95;
    }

    .btn-confirm.orange { background: #EA580C; }
    .btn-confirm.blue { background: #2563EB; }
    .btn-confirm.red { background: #DC2626; }

    /* RESPONSIVIDADE */
    @media (max-width: 1023px) {
        .cashier-hero-card {
            flex-direction: column;
            align-items: stretch;
            gap: 16px;
        }

        .hero-actions-cluster {
            justify-content: flex-start;
        }

        .cashier-kpi-bar {
            grid-template-columns: repeat(2, 1fr);
        }
    }

    @media (max-width: 640px) {
        .cashier-container {
            padding: 16px;
        }

        .cashier-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
        }

        .header-actions {
            width: 100%;
            justify-content: space-between;
        }

        .cashier-kpi-bar {
            grid-template-columns: 1fr;
        }

        .hero-actions-cluster {
            display: grid;
            grid-template-columns: 1fr 1fr;
            width: 100%;
        }

        .quick-btn {
            justify-content: center;
        }
    }

    /* SPIN HELPER */
    :global(.spin) {
        animation: spin 1s linear infinite;
        display: inline-flex;
    }

    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }
</style>
